import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { DEFAULT_PAGE_SIZE_OPTIONS, Error, CustomHttpErrorResponse } from '../../../../../common/constants';
import { TransportManagementLoadFetch, TransportManagementLoadInitialState } from './transport-management-load.actions';
import { Observable, of } from 'rxjs';
import { TransportManagementLoadService } from './transport-management-load.service';
import { NotificationService } from '../../../../../common/services';
import { catchError, map } from 'rxjs/operators';
import { TransportManagementTileItem, TransportManagementTile } from '../models';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportManagementLoadStateModel {
  items: TransportManagementTileItem[];
  page: number;
  size: number;
  isMoreButtonVisible: boolean;
  loaded: boolean;
  isReportLimitationExceeded: boolean;
}

const initialState: TransportManagementLoadStateModel = {
  items: [],
  page: 0,
  size: DEFAULT_PAGE_SIZE_OPTIONS[1],
  isMoreButtonVisible: false,
  loaded: false,
  isReportLimitationExceeded: false,
};

@State({
  name: 'transportManagementLoad',
  defaults: initialState,
})
@Injectable()
export class TransportManagementLoadState {
  @Selector()
  static getState(state: TransportManagementLoadStateModel): TransportManagementLoadStateModel {
    return state;
  }

  @Selector()
  static getItems(state: TransportManagementLoadStateModel): TransportManagementTileItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: TransportManagementLoadStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isMoreButtonVisible(state: TransportManagementLoadStateModel): boolean {
    return state.isMoreButtonVisible;
  }

  @Selector()
  static isReportLimitationExceeded(state: TransportManagementLoadStateModel): boolean {
    return state.isReportLimitationExceeded;
  }

  constructor(
    private transportManagementLoadService: TransportManagementLoadService,
    private notificationService: NotificationService,
  ) {}

  @Action(TransportManagementLoadFetch)
  getLoadList(
    ctx: StateContext<TransportManagementLoadStateModel>,
    { payload }: TransportManagementLoadFetch,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementLoadService.getLoadList(payload).pipe(
      map((list: TransportManagementTile) => {
        const { page } = payload;
        ctx.patchState({
          items: page ? ctx.getState().items.concat(list.items) : list.items,
          isMoreButtonVisible: !!list.items?.length,
          page,
          loaded: true,
          isReportLimitationExceeded: list.reportLimitationExceeded,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementLoadInitialState)
  setInitialState(ctx: StateContext<TransportManagementLoadStateModel>): void {
    ctx.setState(initialState);
  }
}
